import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, isTeacher, Student } from '../../../utils/firebase';
import { doc, updateDoc, addDoc, collection, query, where, getDocs, deleteDoc, getDoc, onSnapshot, setDoc, orderBy, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db, COLLECTIONS, upsertUsersRootProfile } from '../../../utils/firebase';
import { getSubjectName } from '../../../utils/subjectUtils';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import TrashIcon from '../../../components/icons/TrashIcon';
import DownIcon from '../../../components/icons/DownIcon';
import SearchIcon from '../../../components/icons/SearchIcon';
import DividerIcon from '../../../components/icons/DividerIcon';
import ThreeDotLengthIcon from '../../../components/icons/3DotLengthIcon';
import ClassIcon from '../../../components/icons/ClassIcon';
import AddNewClassDialog from '../../../components/ui/AddNewClassDialog';
import ClassCodeDialog from '../../../components/ui/ClassCodeDialog';

interface SubjectStudent {
  id: string;
  name: string;
  email: string;
  phone?: string;
  grade: number;
  class: number;
  number: number;
  stampCount: number;
  subjectPoints: number;
  lastActivity: Date;
  isActive: boolean;
  avatar?: string;
  createdAt?: Date;
}

interface PendingStudent {
  id: string;
  uid: string;
  name: string;
  email: string;
  phone?: string;
  grade: number;
  class: number;
  number: number;
  schoolId: string;
  subjectId: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
}

interface SubjectClass {
  id: string;
  grade: number;
  class: number;
  studentCount: number;
}

const SubjectClassManagement: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<SubjectStudent[]>([]);
  const [pendingStudents, setPendingStudents] = useState<PendingStudent[]>([]);
  const [subjectClasses, setSubjectClasses] = useState<SubjectClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'list' | 'pending'>('list');
  const [selectedGrade, setSelectedGrade] = useState<'all' | 1 | 2 | 3>('all');
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [gradeFilterOpen, setGradeFilterOpen] = useState(false);
  const [openClassCodeDialog, setOpenClassCodeDialog] = useState(false);
  const [selectedClassCode, setSelectedClassCode] = useState<string | null>(null);
  const [selectedClassInfo, setSelectedClassInfo] = useState<{ grade: number; class: number } | null>(null);

  // 교과별 학생 데이터 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    const loadSubjectStudents = async () => {
      try {
        setLoading(true);
        
        // 교과별 학생 컬렉션에서 데이터 로드
        const studentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
        const studentsQuery = query(studentsRef, orderBy('name'));
        const unsubscribe = onSnapshot(studentsQuery, (snapshot) => {
          const studentsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              grade: data.grade || 1,
              class: data.class || 1,
              number: data.number || 1,
              stampCount: data.stampCount?.count || data.stampCount || 0,
              subjectPoints: data.subjectPoints || 0,
              lastActivity: data.lastActivity?.toDate() || new Date(),
              isActive: data.isActive !== false,
              avatar: data.avatar,
              createdAt: data.createdAt?.toDate() || new Date()
            } as SubjectStudent;
          });
          setStudents(studentsData);
          setLoading(false);
        });

        return unsubscribe;
      } catch (err) {
        console.error('교과 학생 데이터 로드 오류:', err);
        setError('교과 학생 데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    loadSubjectStudents();
  }, [currentUser]);

  // 승인 대기 학생 목록 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser) || !currentUser.subjectId) return;

    const loadPendingStudents = async () => {
      try {
        // student_requests 컬렉션에서 승인 대기 중인 학생 목록 가져오기
        const requestsRef = collection(db, COLLECTIONS.STUDENT_REQUESTS);
        const q = query(
          requestsRef,
          where('schoolId', '==', currentUser.schoolId),
          where('subjectId', '==', currentUser.subjectId),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const studentsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              uid: doc.id,
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              grade: data.grade || 1,
              class: data.class || 1,
              number: data.number || 1,
              schoolId: data.schoolId || '',
              subjectId: data.subjectId || '',
              status: data.status || 'pending',
              createdAt: data.createdAt?.toDate() || new Date()
            } as PendingStudent;
          });
          setPendingStudents(studentsData);
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('승인 대기 학생 목록 로드 오류:', err);
      }
    };

    loadPendingStudents();
  }, [currentUser]);

  // 담당 교실 목록 로드 (교사가 담당하는 교실들)
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    // Firestore에서 교사가 담당하는 교실 목록 불러오기
    const teacherClassesRef = collection(
      db,
      `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectClasses`
    );
    const unsubscribe = onSnapshot(teacherClassesRef, (snapshot) => {
      const classesFromFirestore = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          grade: data.grade || 1,
          class: data.class || 1,
          studentCount: 0 // 초기값, 나중에 학생 수로 업데이트
        } as SubjectClass;
      });

      // 학생 데이터와 병합하여 학생 수 계산
      const classesMap = new Map<string, { grade: number; class: number; count: number }>();
      
      // Firestore에서 불러온 교실들로 맵 초기화
      classesFromFirestore.forEach(classInfo => {
        const key = `${classInfo.grade}_${classInfo.class}`;
        classesMap.set(key, { grade: classInfo.grade, class: classInfo.class, count: 0 });
      });

      // 학생 데이터로부터 학생 수 계산
      students.forEach(student => {
        const key = `${student.grade}_${student.class}`;
        if (classesMap.has(key)) {
          const classInfo = classesMap.get(key)!;
          classInfo.count++;
        }
      });

      // 학생이 없는 교실도 포함하여 배열 생성
      const classesArray = Array.from(classesMap.entries()).map(([key, value]) => ({
        id: key,
        grade: value.grade,
        class: value.class,
        studentCount: value.count
      }));

      setSubjectClasses(classesArray);
    }, (err) => {
      console.error('교실 목록 로드 오류:', err);
    });
    
    return () => unsubscribe();
  }, [currentUser, students]);

  // 검색 및 필터링
  const filteredStudents = useMemo(() => {
    const allStudents: (SubjectStudent | PendingStudent)[] = selectedTab === 'list' ? students : pendingStudents;
    let filtered: (SubjectStudent | PendingStudent)[] = [...allStudents];
    
    // 학년 필터
    if (selectedGrade !== 'all') {
      filtered = filtered.filter((student: SubjectStudent | PendingStudent) => student.grade === selectedGrade);
    }
    
    // 검색 필터
    if (searchTerm) {
      filtered = filtered.filter((student: SubjectStudent | PendingStudent) =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${student.grade}학년 ${student.class}반 ${student.number}번`.includes(searchTerm)
      );
    }
    
    return filtered;
  }, [students, pendingStudents, selectedTab, selectedGrade, searchTerm]);

  // 학생 승인 처리
  const handleApproveStudent = async (student: PendingStudent) => {
    if (!currentUser || !isTeacher(currentUser)) return;

    try {
      // student_requests 업데이트
      const requestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
      await updateDoc(requestRef, {
        status: 'approved',
        updatedAt: serverTimestamp()
      });

      // 교과별 students 컬렉션에 학생 추가
      const studentData = {
        name: student.name,
        email: student.email,
        phone: student.phone || '',
        grade: student.grade,
        class: student.class,
        number: student.number,
        stampCount: { count: 0 },
        subjectPoints: 0,
        lastActivity: new Date(),
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        uid: student.uid
      };

      await addDoc(
        collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`),
        studentData
      );

      const lt = (student as { loginType?: string }).loginType;
      await upsertUsersRootProfile({
        uid: student.uid,
        email: student.email,
        name: student.name,
        role: 'student',
        schoolId: student.schoolId || currentUser.schoolId,
        loginType: lt === 'password' || !lt ? 'local' : lt,
        isActive: true,
      });

      setSuccess(`${student.name} 학생이 승인되었습니다.`);
    } catch (err) {
      console.error('학생 승인 오류:', err);
      setError('학생 승인 중 오류가 발생했습니다.');
    }
  };

  // 학생 삭제
  const handleDeleteStudent = async (student: SubjectStudent | PendingStudent) => {
    if (!currentUser || !isTeacher(currentUser)) return;

    if (!window.confirm(`${student.name} 학생을 정말 삭제하시겠습니까?`)) return;

    try {
      if (selectedTab === 'pending') {
        // 승인 대기 목록에서 삭제
        await deleteDoc(doc(db, COLLECTIONS.STUDENT_REQUESTS, (student as PendingStudent).uid));
      } else {
        // 학생 리스트에서 삭제
        await deleteDoc(doc(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`, student.id));
      }
      setSuccess(`${student.name} 학생이 삭제되었습니다.`);
    } catch (err) {
      console.error('학생 삭제 오류:', err);
      setError('학생 삭제 중 오류가 발생했습니다.');
    }
  };

  // 모두 승인 처리
  const handleApproveAll = async () => {
    if (!currentUser || !isTeacher(currentUser)) return;

    try {
      const batch = writeBatch(db);
      
      for (const student of pendingStudents) {
        // student_requests 업데이트
        const requestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
        batch.update(requestRef, {
          status: 'approved',
          updatedAt: serverTimestamp()
        });

        // 교과별 students 컬렉션에 학생 추가
        const studentData = {
          name: student.name,
          email: student.email,
          phone: student.phone || '',
          grade: student.grade,
          class: student.class,
          number: student.number,
          stampCount: { count: 0 },
          subjectPoints: 0,
          lastActivity: new Date(),
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          uid: student.uid
        };

        const studentRef = doc(
          collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`)
        );
        batch.set(studentRef, studentData);
      }

      await batch.commit();

      await Promise.all(
        pendingStudents.map((student) => {
          const lt = (student as { loginType?: string }).loginType;
          return upsertUsersRootProfile({
            uid: student.uid,
            email: student.email,
            name: student.name,
            role: 'student',
            schoolId: student.schoolId || currentUser.schoolId,
            loginType: lt === 'password' || !lt ? 'local' : lt,
            isActive: true,
          });
        })
      );

      setSuccess('모든 학생이 승인되었습니다.');
    } catch (err) {
      console.error('모두 승인 오류:', err);
      setError('모두 승인 중 오류가 발생했습니다.');
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  };

  // 학급 코드 가져오기 및 다이얼로그 열기
  const handleShowClassCode = async (classInfo: SubjectClass) => {
    if (!currentUser || !isTeacher(currentUser)) return;

    try {
      setError(null);
      
      // SUBJECT_CODES 컬렉션에서 해당 학급의 코드 찾기
      const subjectCodeRef = collection(db, COLLECTIONS.SUBJECT_CODES);
      const q = query(
        subjectCodeRef,
        where('schoolId', '==', currentUser.schoolId),
        where('subjectId', '==', currentUser.subjectId),
        where('grade', '==', classInfo.grade),
        where('class', '==', classInfo.class)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const codeData = querySnapshot.docs[0].data();
        setSelectedClassCode(codeData.code);
        setSelectedClassInfo({ grade: classInfo.grade, class: classInfo.class });
        setOpenClassCodeDialog(true);
      } else {
        setError(`${classInfo.grade}학년 ${classInfo.class}반의 교과 코드를 찾을 수 없습니다.`);
      }
    } catch (err) {
      console.error('학급 코드 가져오기 오류:', err);
      setError('학급 코드를 가져오는 중 오류가 발생했습니다.');
    }
  };

  const handleCloseClassCodeDialog = () => {
    setOpenClassCodeDialog(false);
    setSelectedClassCode(null);
    setSelectedClassInfo(null);
  };

  if (!currentUser || !isTeacher(currentUser)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{ 
          color: 'var(--Color-status-negative, #FF3838)', 
          padding: '20px', 
          border: '1px solid #FF3838', 
          borderRadius: '8px' 
        }}>
          교사 권한이 필요합니다.
        </div>
      </div>
    );
  }

  const subjectName = currentUser.subjectId ? getSubjectName(currentUser.subjectId) : '교과';

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative'
    }}>
      {/* 최상위 컨테이너 */}
      <div style={{
        width: '100%',
        maxWidth: '1280px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* 교실관리 제목 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px'
        }}>
          <h1 style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '32px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '48px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            교실 관리
            <DividerIcon width={2} height={28} color="#BABABA" />
            <span style={{
              color: 'var(--Color-text-brand-color, #FF6F0F)',
              fontSize: '32px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '48px'
            }}>
              {subjectName}
            </span>
          </h1>
          <Button
            variant="submit"
            width="152px"
            height="40px"
            size="medium"
            onClick={() => setOpenClassDialog(true)}
            leftIcon={<ClassIcon width={16} height={16} color="white" />}
          >
            담당 교실 만들기
          </Button>
        </div>

        {/* 하위 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}>
          {/* 담당 교실 섹션 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
          <h2 style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '18px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '27px',
            margin: 0
          }}>
            담당 교실
          </h2>
          <div style={{
            display: 'flex',
            gap: '12px',
            overflowX: 'auto',
            paddingBottom: '10px'
          }}>
            {subjectClasses.map((classInfo) => (
              <Card
                key={classInfo.id}
                width="235px"
                height="85px"
                padding="16px"
                gap="8px"
                onClick={() => navigate(`/subject/teacher/class/${classInfo.grade}/${classInfo.class}`)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '18px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '27px'
                    }}>
                      {classInfo.grade}학년 {classInfo.class}반
                    </div>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '24px'
                    }}>
                      {classInfo.studentCount}명
                    </div>
                  </div>
                  <div 
                    style={{
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowClassCode(classInfo);
                    }}
                  >
                    <ThreeDotLengthIcon width={3} height={13} color="#0D0D0D" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* 담당 교과 학생 관리 섹션 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2 style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '18px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '27px',
            margin: 0
          }}>
            담당 교과 학생 관리
          </h2>

          {/* 탭 및 필터 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              gap: '16px'
            }}>
              <button
                onClick={() => setSelectedTab('list')}
                style={{
                  height: '40px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: selectedTab === 'list' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                  color: selectedTab === 'list' ? 'white' : 'var(--Color-text-normal, #161616)',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  cursor: 'pointer'
                }}
              >
                학생 리스트
              </button>
              <button
                onClick={() => setSelectedTab('pending')}
                style={{
                  height: '40px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: selectedTab === 'pending' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                  color: selectedTab === 'pending' ? 'white' : 'var(--Color-text-normal, #161616)',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  cursor: 'pointer'
                }}
              >
                승인 대기 목록
              </button>
            </div>
            {selectedTab === 'pending' && pendingStudents.length > 0 && (
              <Button
                variant="submit"
                width="118px"
                height="40px"
                size="medium"
                onClick={handleApproveAll}
              >
                모두 승인
              </Button>
            )}
          </div>

          {/* 필터 및 검색 */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            {/* 학년 필터 */}
            <div style={{
              position: 'relative'
            }}>
              <button
                onClick={() => setGradeFilterOpen(!gradeFilterOpen)}
                style={{
                  width: '112px',
                  height: '40px',
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  color: 'var(--Color-text-normal, #161616)'
                }}
              >
                <span>{selectedGrade === 'all' ? '전체 학년' : `${selectedGrade}학년`}</span>
                <DownIcon width={24} height={24} fill="#161616" />
              </button>
              {gradeFilterOpen && (
                <div style={{
                  position: 'absolute',
                  top: '44px',
                  left: 0,
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  minWidth: '112px'
                }}>
                  {(['all', 1, 2, 3] as const).map((grade) => (
                    <div
                      key={grade}
                      onClick={() => {
                        setSelectedGrade(grade);
                        setGradeFilterOpen(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        color: selectedGrade === grade ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)',
                        backgroundColor: selectedGrade === grade ? 'var(--Color-background-sub, #F8F8F8)' : 'transparent'
                      }}
                    >
                      {grade === 'all' ? '전체 학년' : `${grade}학년`}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 검색 바 */}
            <div style={{
              flex: 1,
              height: '40px',
              padding: '8px 16px',
              background: 'white',
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid var(--Color-line-normal, #F0F0F0)'
            }}>
              <SearchIcon width={20} height={20} color="#222222" />
              <input
                type="text"
                placeholder="검색어를 입력해 주세요.(이름 또는 이메일)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  color: 'var(--Color-text-assistive, #989898)',
                  background: 'transparent'
                }}
              />
            </div>
          </div>

          {/* 에러/성공 메시지 */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#FFEBEE',
              color: '#C62828',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Pretendard'
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              padding: '12px 16px',
              background: '#E8F5E9',
              color: '#2E7D32',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Pretendard'
            }}>
              {success}
            </div>
          )}

          {/* 테이블 */}
          <div style={{
            padding: '16px 20px',
            background: 'white',
            borderRadius: '16px'
          }}>
            {/* 테이블 헤더 */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              paddingBottom: '12px'
            }}>
              <div style={{ width: '103px', padding: '0 16px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px' }}>No</div>
              <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                학년
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.6 3.6L8 8.8L12.4 3.6H3.6Z" fill="#989898"/>
                </svg>
              </div>
              <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                반
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.6 3.6L8 8.8L12.4 3.6H3.6Z" fill="#989898"/>
                </svg>
              </div>
              <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                학번
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.6 3.6L8 8.8L12.4 3.6H3.6Z" fill="#989898"/>
                </svg>
              </div>
              <div style={{ width: '110px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                이름
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.6 3.6L8 8.8L12.4 3.6H3.6Z" fill="#989898"/>
                </svg>
              </div>
              <div style={{ width: '230px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px' }}>이메일</div>
              <div style={{ width: '162px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px' }}>전화</div>
              <div style={{ width: '170px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                신청일시
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.6 3.6L8 8.8L12.4 3.6H3.6Z" fill="#989898"/>
                </svg>
              </div>
              <div style={{ width: '130px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px' }}>승인</div>
              <div style={{ width: '65px', padding: '0 8px', color: 'var(--Color-text-assistive, #989898)', fontSize: '13px', fontFamily: 'Pretendard', fontWeight: '500', lineHeight: '19.5px' }}>관리</div>
            </div>

            {/* 테이블 바디 */}
            {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'Pretendard'
              }}>
                로딩 중...
              </div>
            ) : filteredStudents.length === 0 ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'Pretendard'
              }}>
                {selectedTab === 'list' ? '등록된 학생이 없습니다.' : '승인 대기 중인 학생이 없습니다.'}
              </div>
            ) : (
              filteredStudents.map((student, index) => (
                <div
                  key={student.id}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    padding: '12px 0',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ width: '103px', padding: '0 18px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.grade}학년
                  </div>
                  <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.class}반
                  </div>
                  <div style={{ width: '90px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.number}번
                  </div>
                  <div style={{ width: '110px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.name}
                  </div>
                  <div style={{ width: '230px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.email}
                  </div>
                  <div style={{ width: '162px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {student.phone || '-'}
                  </div>
                  <div style={{ width: '170px', padding: '0 8px', color: 'var(--Color-text-normal, #161616)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px' }}>
                    {formatDate(selectedTab === 'list' ? (student as SubjectStudent).createdAt! : (student as PendingStudent).createdAt)}
                  </div>
                  <div style={{ width: '130px', padding: '0 8px' }}>
                    {selectedTab === 'pending' && (
                      <button
                        onClick={() => handleApproveStudent(student as PendingStudent)}
                        style={{
                          width: '66px',
                          height: '25px',
                          padding: '4px 8px',
                          background: 'var(--Color-fill-normal, #293044)',
                          borderRadius: '4px',
                          border: 'none',
                          color: 'white',
                          fontSize: '11px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '16.5px',
                          cursor: 'pointer'
                        }}
                      >
                        승인
                      </button>
                    )}
                  </div>
                  <div style={{ width: '65px', padding: '0 8px' }}>
                    <button
                      onClick={() => handleDeleteStudent(student)}
                      style={{
                        width: '20px',
                        height: '20px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <TrashIcon width={16} height={16} color="#989898" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        </div>
      </div>

      {/* 담당 교실 만들기 다이얼로그 */}
      <AddNewClassDialog
        isOpen={openClassDialog}
        onClose={() => setOpenClassDialog(false)}
        onAdd={async (data) => {
          if (!currentUser || !isTeacher(currentUser)) return;

          try {
            // 교실 코드가 없으면 에러
            if (!data.code) {
              setError('교실 코드가 생성되지 않았습니다.');
              return;
            }

            // 교실 코드를 subjectCodes 컬렉션에 저장
            const subjectCodeRef = collection(db, COLLECTIONS.SUBJECT_CODES);
            const existingQuery = query(
              subjectCodeRef,
              where('code', '==', data.code),
              where('schoolId', '==', currentUser.schoolId),
              where('subjectId', '==', currentUser.subjectId)
            );
            const existingSnapshot = await getDocs(existingQuery);

            if (existingSnapshot.empty) {
              // 교실 코드 저장
              await addDoc(collection(db, COLLECTIONS.SUBJECT_CODES), {
                code: data.code,
                schoolId: currentUser.schoolId,
                subjectId: currentUser.subjectId,
                teacherId: currentUser.uid,
                grade: data.grade,
                class: data.class,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            }

            // 교사가 담당하는 교실 목록에 추가
            const teacherClassRef = doc(
              db,
              `schools/${currentUser.schoolId}/teachers/${currentUser.uid}/subjectClasses/${data.grade}_${data.class}`
            );
            await setDoc(teacherClassRef, {
              grade: data.grade,
              class: data.class,
              code: data.code,
              subjectId: currentUser.subjectId,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            }, { merge: true });

            setSuccess(`${data.grade}학년 ${data.class}반 교실이 추가되었습니다.`);
            setOpenClassDialog(false);
            
            // 교실 목록 새로고침을 위해 students 목록이 업데이트되도록 함
            // (실제로는 교실 목록이 별도로 관리되므로 즉시 반영됨)
          } catch (err) {
            console.error('교실 추가 오류:', err);
            setError('교실 추가 중 오류가 발생했습니다.');
          }
        }}
      />

      {/* 학급 코드 다이얼로그 */}
      {selectedClassCode && selectedClassInfo && (
        <ClassCodeDialog
          isOpen={openClassCodeDialog}
          onClose={handleCloseClassCodeDialog}
          classCode={selectedClassCode}
          grade={selectedClassInfo.grade}
          class={selectedClassInfo.class}
          scale={1}
        />
      )}
    </div>
  );
};

export default SubjectClassManagement;
